import { type Attributes, type AttributeValue, type Tracer, SpanStatusCode } from '@opentelemetry/api';
import type { LogValue, TelemetryLogger, ThrownValue } from './types.js';

const MAX_MESSAGE_LENGTH = 1000;
const MAX_VALUE_LENGTH = 500;
const MAX_STACK_LENGTH = 4000;
const MAX_BREADCRUMBS = 20;
const MAX_ATTRIBUTES = 40;
const MAX_ERRORS_PER_WINDOW = 20;
const RATE_WINDOW_MS = 60_000;

export type ErrorReporterOptions = {
  /** Injectable for tests. */
  now?: () => number;
};

type Breadcrumb = { at: number; message: string };

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

function toError(thrown: ThrownValue): Error {
  if (thrown instanceof Error) return thrown;
  if (typeof thrown === 'string') return new Error(truncate(thrown, MAX_MESSAGE_LENGTH));
  try {
    return new Error(truncate(JSON.stringify(thrown) ?? String(thrown), MAX_MESSAGE_LENGTH));
  } catch {
    return new Error('Non-error value thrown');
  }
}

/** Primitives pass through; objects become bounded JSON, so an attribute can never hold a live object. */
function toAttributeValue(value: LogValue): AttributeValue | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return truncate(value, MAX_VALUE_LENGTH);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  try {
    return truncate(JSON.stringify(value), MAX_VALUE_LENGTH);
  } catch {
    return undefined;
  }
}

function addPrefixed(
  attributes: Attributes,
  prefix: string,
  bag: Record<string, LogValue> | undefined
): void {
  for (const [key, value] of Object.entries(bag ?? {})) {
    if (Object.keys(attributes).length >= MAX_ATTRIBUTES) return;
    const attribute = toAttributeValue(value);
    if (attribute !== undefined) attributes[`${prefix}.${key}`] = attribute;
  }
}

/**
 * Reports errors as OpenTelemetry spans with an `exception` event, so they land in whatever trace backend the
 * deployment uses, joined to the active trace when there is one. Bounded on every axis: attribute count and size,
 * breadcrumb history, duplicate reports of one error, and reports per minute.
 */
export function createErrorReporter(tracer: Tracer, options: ErrorReporterOptions = {}): TelemetryLogger {
  const now = options.now ?? Date.now;
  const breadcrumbs: Breadcrumb[] = [];
  const contexts = new Map<string, Record<string, LogValue>>();
  const reported = new WeakSet<object>();
  let windowStart = now();
  let reportsInWindow = 0;
  let dropped = 0;

  const withinRateLimit = (): boolean => {
    const current = now();
    if (current - windowStart >= RATE_WINDOW_MS) {
      windowStart = current;
      reportsInWindow = 0;
    }
    reportsInWindow += 1;
    return reportsInWindow <= MAX_ERRORS_PER_WINDOW;
  };

  return {
    addBreadcrumb: (breadcrumb) => {
      if (!breadcrumb.message) return;
      breadcrumbs.push({ at: now(), message: truncate(breadcrumb.message, MAX_VALUE_LENGTH) });
      if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
    },

    setContext: (name, context) => {
      contexts.set(name, context);
    },

    captureMessage: (message) => {
      if (!withinRateLimit()) return;
      const span = tracer.startSpan('message', {
        attributes: { 'app.message': truncate(message, MAX_MESSAGE_LENGTH) },
      });
      span.end();
    },

    captureException: (thrown, context) => {
      if (typeof thrown === 'object' && thrown !== null) {
        // The same error is often seen twice (React's handler and the window's); report it once.
        if (reported.has(thrown)) return;
        reported.add(thrown);
      }
      if (!withinRateLimit()) {
        dropped += 1;
        return;
      }

      const error = toError(thrown);
      const attributes: Attributes = {
        'exception.type': error.name,
        'exception.message': truncate(error.message, MAX_MESSAGE_LENGTH),
        'app.error.level': context?.level ?? 'error',
      };
      if (dropped > 0) {
        attributes['app.errors.dropped'] = dropped;
        dropped = 0;
      }
      addPrefixed(attributes, 'app.tag', context?.tags);
      addPrefixed(attributes, 'app.extra', context?.extra);
      for (const [name, bag] of contexts) {
        addPrefixed(attributes, `app.context.${name}`, bag);
      }
      if (breadcrumbs.length > 0) {
        attributes['app.breadcrumbs'] = truncate(JSON.stringify(breadcrumbs), MAX_MESSAGE_LENGTH * 2);
      }

      const span = tracer.startSpan('exception', { attributes });
      span.recordException({
        name: error.name,
        message: truncate(error.message, MAX_MESSAGE_LENGTH),
        stack: error.stack ? truncate(error.stack, MAX_STACK_LENGTH) : undefined,
      });
      span.setStatus({ code: SpanStatusCode.ERROR, message: truncate(error.message, MAX_VALUE_LENGTH) });
      span.end();
    },
  };
}
